/**
 * 소비기한·재고 상태 — Design Tokens + Label Tokens 단일 소스
 * Inventory, Layout, DailyReport 공통
 */
import colors from './colors';
import {
  getStatusLabelStyle,
  getStockStatusTone,
  type StatusTone,
} from './labels';

export type ExpirySemantic = 'error' | 'warning' | 'info' | 'success' | 'default';
export type ExpiryStatus =
  | 'expired'
  | 'critical'
  | 'warning'
  | 'attention'
  | 'safe'
  | 'unknown';
export type ExpiryStatusCode = ExpiryStatus | 'today' | 'info' | 'ok' | 'none';
export type LayoutExpiryColor = 'error' | 'warning' | 'default';

export interface ExpiryLabelStyle {
  backgroundColor: string;
  color: string;
  fontSize: string;
  text?: string;
}

export interface ExpiryStatusResult {
  status: ExpiryStatus;
  tone: StatusTone;
  daysRemaining: number | null;
  label: string;
}

export interface ExpiryDdayResult {
  text: string;
  color: string;
  bgcolor: string;
  daysLeft: number | null;
  status: ExpiryStatusCode;
  semantic: ExpirySemantic;
  tone: StatusTone;
}

function toneStyle(tone: StatusTone): Pick<ExpiryLabelStyle, 'backgroundColor' | 'color'> {
  const style = getStatusLabelStyle(tone);
  return { backgroundColor: style.background, color: style.text };
}

function semanticStyle(semantic: ExpirySemantic): Pick<ExpiryLabelStyle, 'backgroundColor' | 'color'> {
  switch (semantic) {
    case 'error':
      return toneStyle('error');
    case 'warning':
      return toneStyle('warning');
    case 'info':
      return toneStyle('info');
    case 'success':
      return toneStyle('success');
    default:
      return toneStyle('neutral');
  }
}

function diffDaysFromExpiry(expiryDate: string): number {
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function labelFromDiffDays(diffDays: number, showExpiredDays = false): string {
  if (diffDays < 0) {
    return showExpiredDays ? `만료 (${Math.abs(diffDays)}일 지남)` : '만료';
  }
  if (diffDays === 0) return '오늘 만료';
  return `D-${diffDays}`;
}

function resolveStatusFromDiffDays(diffDays: number): ExpiryStatus {
  if (diffDays < 0) return 'expired';
  if (diffDays <= 2) return 'critical';
  if (diffDays <= 6) return 'warning';
  if (diffDays <= 13) return 'attention';
  return 'safe';
}

function toneFromStatus(status: ExpiryStatus): StatusTone {
  switch (status) {
    case 'expired':
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'attention':
      return 'info';
    case 'safe':
      return 'success';
    default:
      return 'neutral';
  }
}

function semanticFromStatus(status: ExpiryStatus): ExpirySemantic {
  switch (status) {
    case 'expired':
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'attention':
      return 'info';
    case 'safe':
      return 'success';
    default:
      return 'default';
  }
}

/** §6 중앙 Helper — 날짜 계산 기준은 diffDaysFromExpiry 유지 */
export function resolveExpiryStatus(
  expiryDate: string | null | undefined,
  skipExpiryDate?: boolean,
  options?: { showExpiredDays?: boolean },
): ExpiryStatusResult {
  if (!expiryDate || skipExpiryDate) {
    return {
      status: 'unknown',
      tone: 'neutral',
      daysRemaining: null,
      label: '미설정',
    };
  }

  const daysRemaining = diffDaysFromExpiry(expiryDate);
  const status = resolveStatusFromDiffDays(daysRemaining);

  return {
    status,
    tone: toneFromStatus(status),
    daysRemaining,
    label: labelFromDiffDays(daysRemaining, options?.showExpiredDays),
  };
}

/** D-0~2=error, D-3~6=warning, D-7~13=info, D-14+=success */
export function semanticFromDiffDays(diffDays: number): ExpirySemantic {
  return semanticFromStatus(resolveStatusFromDiffDays(diffDays));
}

function statusCodeFromDiffDays(diffDays: number): ExpiryStatusCode {
  const status = resolveStatusFromDiffDays(diffDays);
  if (status === 'critical' && diffDays === 0) return 'today';
  if (status === 'attention') return 'info';
  if (status === 'safe') return 'ok';
  return status;
}

function textFromDiffDays(diffDays: number, showExpiredDays = false): string {
  return labelFromDiffDays(diffDays, showExpiredDays);
}

export function computeExpiryDday(
  expiryDate: string | null | undefined,
  skipExpiryDate?: boolean,
  options?: { showExpiredDays?: boolean },
): ExpiryDdayResult {
  const resolved = resolveExpiryStatus(expiryDate, skipExpiryDate, options);

  if (resolved.status === 'unknown') {
    const s = semanticStyle('default');
    return {
      text: resolved.label,
      color: s.color,
      bgcolor: s.backgroundColor,
      daysLeft: null,
      status: 'none',
      semantic: 'default',
      tone: 'neutral',
    };
  }

  const semantic = semanticFromStatus(resolved.status);
  const s = semanticStyle(semantic);

  return {
    text: resolved.label,
    color: s.color,
    bgcolor: s.backgroundColor,
    daysLeft: resolved.daysRemaining,
    status: statusCodeFromDiffDays(resolved.daysRemaining as number),
    semantic,
    tone: resolved.tone,
  };
}

export function computeExpiryDdayFromIngredient(
  ingredient: { expiryDate?: string | null; skipExpiryDate?: boolean },
  options?: { showExpiredDays?: boolean },
): ExpiryDdayResult {
  return computeExpiryDday(ingredient.expiryDate, ingredient.skipExpiryDate, options);
}

export function getExpiryDdayInfo(
  expiryDate: string | null | undefined,
  skipExpiryDate: boolean,
) {
  const result = computeExpiryDday(expiryDate, skipExpiryDate, { showExpiredDays: true });
  return { text: result.text, color: result.color, daysLeft: result.daysLeft };
}

export function getLayoutExpiryColor(semantic: ExpirySemantic): LayoutExpiryColor {
  if (semantic === 'error') return 'error';
  if (semantic === 'warning') return 'warning';
  return 'default';
}

export function computeLayoutExpiryDDay(
  ingredient: { expiryDate?: string | null; skipExpiryDate?: boolean },
): { text: string; color: LayoutExpiryColor } {
  const result = computeExpiryDdayFromIngredient(ingredient, { showExpiredDays: true });
  return {
    text: result.text,
    color: getLayoutExpiryColor(result.semantic),
  };
}

export function getExpiryChipSx(color: LayoutExpiryColor) {
  if (color === 'error') {
    const s = semanticStyle('error');
    return { color: s.color, bgcolor: s.backgroundColor };
  }
  if (color === 'warning') {
    const s = semanticStyle('warning');
    return { color: s.color, bgcolor: s.backgroundColor };
  }
  const s = toneStyle('neutral');
  return { color: s.color, bgcolor: s.backgroundColor };
}

export function getExpiryChipSxFromSemantic(semantic: ExpirySemantic) {
  const s = semanticStyle(semantic);
  return { color: s.color, bgcolor: s.backgroundColor };
}

export function getExpiryChipSxFromTone(tone: StatusTone) {
  const style = getStatusLabelStyle(tone);
  return { color: style.text, bgcolor: style.background };
}

function stockStyleForTone(tone: StatusTone, text: string): ExpiryLabelStyle {
  const style = toneStyle(tone);
  return {
    ...style,
    fontSize: 'inherit',
    text,
  };
}

export const STOCK_STATUS_STYLES: Record<string, ExpiryLabelStyle> = {
  정상: stockStyleForTone('success', '재고정상'),
  부족: stockStyleForTone('error', '재고부족'),
  '확인 필요': stockStyleForTone('warning', '확인 필요'),
  미입력: stockStyleForTone('neutral', '미입력'),
  미설정: stockStyleForTone('neutral', '미설정'),
  '입고 예정': stockStyleForTone('info', '입고 예정'),
  품절: stockStyleForTone('error', '품절'),
  default: stockStyleForTone('neutral', '기본'),
};

function buildExpiryStatusStyles(): Record<string, ExpiryLabelStyle> {
  const styles: Record<string, ExpiryLabelStyle> = {};
  const base = { fontSize: 'inherit' as const };

  styles['만료'] = { ...toneStyle('error'), ...base, text: '만료' };
  styles['오늘 만료'] = { ...toneStyle('error'), ...base, text: '오늘 만료' };
  styles['오늘'] = styles['오늘 만료'];
  styles['D-1'] = { ...toneStyle('error'), ...base, text: 'D-1' };
  styles['D-2'] = { ...toneStyle('error'), ...base, text: 'D-2' };

  for (let d = 3; d <= 6; d++) {
    styles[`D-${d}`] = { ...toneStyle('warning'), ...base, text: `D-${d}` };
  }
  for (let d = 7; d <= 13; d++) {
    styles[`D-${d}`] = { ...toneStyle('info'), ...base, text: `D-${d}` };
  }
  for (const key of ['D-14', 'D-30', 'D-60', 'D-90', 'D-180', 'D-365']) {
    styles[key] = { ...toneStyle('success'), ...base, text: key };
  }

  const noneStyle: ExpiryLabelStyle = { ...toneStyle('neutral'), ...base, text: '미설정' };
  styles['없음'] = noneStyle;
  styles['-'] = { ...noneStyle, text: '-' };
  styles['미설정'] = noneStyle;
  styles.default = { ...noneStyle, text: '기본' };
  return styles;
}

export const EXPIRY_STATUS_STYLES = buildExpiryStatusStyles();

export function getStockStatusStyle(status: string): ExpiryLabelStyle {
  return STOCK_STATUS_STYLES[status] || STOCK_STATUS_STYLES.default;
}

export function getStockStatusToneForLabel(status: string): StatusTone {
  return getStockStatusTone(status);
}

export function getExpiryStatusStyleByLabel(status: string): ExpiryLabelStyle {
  return EXPIRY_STATUS_STYLES[status] || EXPIRY_STATUS_STYLES.default;
}

/** Layout 알림·Inventory '기한만료' 필터 — error+warning (≤6일, 만료 포함) */
export const EXPIRY_ALERT_DAYS = 6;

/** DailyReport 임박 목록 — UI "3일 이내" */
export const EXPIRY_REPORT_DAYS = 3;

export function isExpiringWithinDays(daysLeft: number | null, withinDays = EXPIRY_REPORT_DAYS): boolean {
  return daysLeft !== null && daysLeft <= withinDays;
}

export function isExpiryCritical(daysLeft: number | null): boolean {
  return daysLeft !== null && daysLeft <= 2;
}

export function isExpiryAlert(daysLeft: number | null): boolean {
  return daysLeft !== null && daysLeft <= EXPIRY_ALERT_DAYS;
}

export function isIngredientExpiryAlert(
  ingredient: { expiryDate?: string | null; skipExpiryDate?: boolean },
): boolean {
  if (!ingredient.expiryDate || ingredient.skipExpiryDate) return false;
  return isExpiryAlert(computeExpiryDdayFromIngredient(ingredient).daysLeft);
}

/** status code → MUI palette keys (legacy sx) */
export function getExpiryStatusStyleByCode(status: string) {
  switch (status) {
    case 'expired':
    case 'today':
    case 'critical':
      return { color: 'error.main', backgroundColor: 'error.light', text: '만료' };
    case 'warning':
      return { color: 'warning.main', backgroundColor: 'warning.light', text: '임박' };
    case 'attention':
    case 'info':
      return { color: 'info.main', backgroundColor: 'info.light', text: '관심' };
    case 'safe':
    case 'ok':
      return { color: 'success.main', backgroundColor: 'success.light', text: '양호' };
    default:
      return { color: 'text.secondary', backgroundColor: 'grey.100', text: '미설정' };
  }
}
